<script lang="ts" setup>
import { ref } from "vue";
import { storeToRefs } from "pinia";

import { create } from "@/repositories/ledgers.ts";

import { useAccount } from "@/stores/account.ts";
import { useEditTransaction } from "@/components/EditTransaction/state.ts";

import type { Ledger } from "@/types/account";
import { LedgerFactory, type LedgerResource } from "@/factories/LedgerFactory.ts";

const emit = defineEmits(["close"]);

const account = useAccount();
const state = useEditTransaction();

const { ledgers } = storeToRefs(account);
const { ledger: selectedLedger } = storeToRefs(state);

const name = ref("");
const isDefault = ref(false);

const onCancel = () => emit("close");

const onSubmit = async () => {
  const ledger: Omit<Ledger, "id" | "colour"> = {
    name: name.value,
    isDefault: isDefault.value,
  };

  create(ledger).then((resource: LedgerResource) => {
    const createdLedger = new LedgerFactory().fromJson(resource);

    ledgers.value.push(createdLedger);

    selectedLedger.value = createdLedger;

    emit("close");
  });
};
</script>

<template>
  <div class="p-5 rounded bg-gray-100">
    <form class="flex flex-col space-y-2">
      <div>
        <label class="block text-sm/6 font-medium text-gray-900" for="name"
          >Naam van het account</label
        >
        <div class="mt-2">
          <input
            id="name"
            v-model="name"
            class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            name="name"
            placeholder="Prive"
            type="text"
          />
        </div>
      </div>

      <div class="text-sm/6">
        <div class="flex gap-3">
          <div class="flex h-6 shrink-0 items-center">
            <div class="group grid size-4 grid-cols-1">
              <input
                id="comments"
                v-model="isDefault"
                aria-describedby="comments-description"
                class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                name="comments"
                type="checkbox"
              />
              <svg
                class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  class="opacity-0 group-has-checked:opacity-100"
                  d="M3 8L6 11L11 3.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
                <path
                  class="opacity-0 group-has-indeterminate:opacity-100"
                  d="M3 7H11"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
              </svg>
            </div>
          </div>
          <div class="text-sm/6">
            <label class="font-medium text-gray-900" for="comments">Standaard</label>
            {{ " " }}
            <span id="comments-description" class="text-gray-500"
              >Automatisch selecteren bij nieuwe items</span
            >
          </div>
        </div>
      </div>

      <div class="flex flex-row space-x-2">
        <button
          class="rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          type="button"
          @click="onSubmit"
        >
          Opslaan
        </button>

        <button
          class="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
          type="button"
          @click="onCancel"
        >
          Annuleren
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
button {
  cursor: pointer;
}
</style>
